import React, { useState } from "react";
import { useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  extractErrorMessage,
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type BreadcrumbItem,
} from "@shadowcue/ui";
import UserForm, { type IUserForm } from "./form";

interface NewUserProps {
  currentUser: IUser;
}

const blankUser: IUserForm = {
  email: "",
  password: "",
  passwordConfirm: "",
  admin: false,
};

const axios = newAxiosClient();


const NewUser = ({ currentUser }: NewUserProps) => {
  const [formValues, setFormValues] = useState<IUserForm>(blankUser);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Users", href: "/admin/users" },
    { label: "Create" },
  ];

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    // Check password confirmation match
    if (formValues.password !== formValues.passwordConfirm) {
      setSubmitError("Password and password confirmation must match")
      setSubmitting(false)
      return
    }

    try {
      await axios.post(
        "/api/users",
        { user: formValues },
        {
          headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
          withCredentials: true,
        },
      );
      navigate("/admin/users");
    } catch (error) {
      setSubmitError(
        extractErrorMessage(error, "Failed to save the user. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <UserForm values={formValues} onChange={setFormValues} />

        <StickyActionBar error={submitError ?? undefined}>
          <astryx.Button
            label="Cancel"
            variant="ghost"
            onClick={() => navigate("/admin/users")}
          />
          <astryx.Button
            label="Create user"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
          />
        </StickyActionBar>
      </PageContainer>
    </astryx.Theme>
  );
};

export default NewUser;
